import { useUserSession } from '@solinkcorp/authentication';
import { usePrevious } from '@solinkcorp/solink-components';
import { type IEvent } from 'clients/PlatformApi';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCloudFrontUrl } from 'src/core/video/coreCameraUtils';

/** The id given to the check-in thumbnail, which is the last resort image. */
export const CHECKIN_THUMBNAIL_ID = -1;

export interface S3Thumbnail {
  id: number;
  date: number;
  url: string;
}

export interface S3ThumbnailsInput {
  /** The URL to access camera thumbnails (e.g. `camera.thumbnail`). */
  url?: string;
  /** The check-in thumbnail, used after every other thumbnail fails. */
  checkInUrl?: string;
  images?: IEvent['cameras'][0]['images'];
  /** Called with `false` when the check-in thumbnail fails, and `true` when it becomes the image in use. */
  updateImageProcessingState?: (state: boolean) => void;
}

export interface S3Thumbnails {
  /** The thumbnails that are still usable, in date order. */
  thumbnails: S3Thumbnail[];
  /** The index in `thumbnails` to show now. Always a valid index while `thumbnails` is not empty. */
  currentIndex: number;
  /** Call when an `<img>` fails to load. The failed thumbnail drops out of `thumbnails`. */
  onImageError: (id: number) => void;
}

/**
 * Everything an S3 thumbnail needs to show, with nothing about how it looks.
 *
 * The hook signs one CloudFront URL per thumbnail, drops a thumbnail when its image
 * fails, falls back to the check-in thumbnail once every other one fails, and rotates
 * through the list once per second while more than one thumbnail remains.
 */
export function useS3Thumbnails({
  url,
  checkInUrl,
  images,
  updateImageProcessingState,
}: S3ThumbnailsInput): S3Thumbnails {
  const user = useUserSession();

  // A list of thumbnail IDs to exclude.
  const [excludedThumbnailIds, setExcludedThumbnailIds] = useState<number[]>([]);

  // The current thumbnail to show - this is an index into `thumbnails`.
  const [currentThumbnailIndex, setCurrentThumbnailIndex] = useState(0);

  // Get sorted thumbnails.  Note that this computes pre-signed URLs for each thumbnail.
  // We do not, however, need to recompute this array when the pre-signed URLs
  // expire, as by then the browser will have long ago loaded the images and will
  // be displaying them.  We only need to recompute this array if the passed in
  // url or images actually change.
  const rawThumbnails = useMemo(
    () => getS3Thumbnails(user.accessToken, user.customerId, url, images),
    [user, url, images],
  );
  const prevRawThumbnails = usePrevious(rawThumbnails);

  // If the thumbnails change, reset the excluded thumbnails list.
  useEffect(() => {
    if (rawThumbnails !== prevRawThumbnails) {
      setExcludedThumbnailIds([]);
      setCurrentThumbnailIndex(0);
    }
  }, [rawThumbnails, prevRawThumbnails]);

  // Get a list of usable thumbnails.
  const thumbnails = useMemo(() => {
    let result = rawThumbnails.flatMap((t) => (excludedThumbnailIds.includes(t.id) ? [] : [t]));

    // use the check-in thumbnail if it's not the check-in thumbnail which failed to load
    if (result.length === 0 && checkInUrl) {
      result = [
        {
          id: CHECKIN_THUMBNAIL_ID,
          date: 0,
          url: getCloudFrontUrl(user.customerId, user.accessToken, checkInUrl),
        },
      ];
    }
    return result;
  }, [excludedThumbnailIds, rawThumbnails, checkInUrl, user]);

  // Rotate between thumbnails.
  useEffect(() => {
    if (thumbnails.length > 1) {
      let nextIndex = 0;
      const rotationInterval = setInterval(() => {
        nextIndex++;
        if (nextIndex >= thumbnails.length) {
          nextIndex = 0;
        }
        setCurrentThumbnailIndex(nextIndex);
      }, 1000);

      return () => clearInterval(rotationInterval);
    }
  }, [setCurrentThumbnailIndex, thumbnails]);

  const onImageError = useCallback(
    (id: number) => {
      if (id === CHECKIN_THUMBNAIL_ID) {
        // If we failed to load the checkin thumbnail, call updateImageProcessingState(false).
        updateImageProcessingState?.(false);
      } else if (!excludedThumbnailIds.includes(id)) {
        if (excludedThumbnailIds.length + 1 === rawThumbnails.length) {
          // If we've run out of thumbnails, let the parent know we're going
          // to render the checkin thumbnail so it can blur the thumbnail.
          updateImageProcessingState?.(true);
        }

        // Add the thumbnail ID to the excluded list.
        setExcludedThumbnailIds(excludedThumbnailIds.concat([id]));
      }
    },
    [updateImageProcessingState, rawThumbnails, excludedThumbnailIds],
  );

  // Safety check to make sure `currentIndex` is a valid thumbnail.
  const currentIndex = Math.min(currentThumbnailIndex, thumbnails.length - 1);

  return { thumbnails, currentIndex, onImageError };
}

/**
 * Build the signed thumbnail list for one camera.
 *
 * An event camera usually has an empty `images` array when it has only one image. In
 * that case the function falls back to `url`, which comes from `camera.thumbnail`.
 */
export function getS3Thumbnails(
  jwt: string | undefined,
  tenantId: string | undefined,
  url: string | undefined,
  images: IEvent['cameras'][0]['images'],
): S3Thumbnail[] {
  const thumbnails: S3Thumbnail[] = [];

  if (!jwt || !tenantId) {
    return thumbnails;
  }

  if (images && images.length > 0) {
    images
      .sort((a, b) => {
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      })
      .map(({ date, url }, index) => {
        thumbnails.push({
          id: index,
          date: new Date(date).getTime(),
          url: getCloudFrontUrl(tenantId, jwt, url),
        });
      });
  } else if (url) {
    thumbnails.push({
      id: 0,
      date: new Date().getTime(),
      url: getCloudFrontUrl(tenantId, jwt, url),
    });
  }

  return thumbnails;
}

/** Turn a numeric pixel size into a CSS length. An absent size fills the container. */
export function toCssDimension(value: string | number | undefined): string {
  if (value === undefined) {
    return '100%';
  } else if (typeof value === 'number') {
    return `${value}px`;
  }
  return value;
}
